import React, { useMemo } from 'react';

type SectorProps = {
    size?: number; // SVG 尺寸（默认 101）
    radius?: number; // 半径（默认 49.5）
    angle: number; // 扇形展开角度（0 ~ 360）
    strokeWidth?: number; // 圆边线宽（默认 2）
};

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
    const rad = ((angleDeg - 90) * Math.PI) / 180.0; // 0° 在正上方
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function fmt(n: number) {
    return Number(n.toFixed(3));
}

const Sector: React.FC<SectorProps> = ({ size = 101, radius = 49.5, angle, strokeWidth = 2 }) => {
    const cx = size / 2;
    const cy = size / 2;

    const sweep = Math.max(0, Math.min(angle, 360)); // clamp 到 [0,360]
    const isFull = sweep >= 360 || Math.abs(sweep - 360) < 1e-6;

    // 填充扇形 path（当不是整圆时）
    const pathCircle = useMemo(() => {
        if (isFull || sweep === 0) return null;

        const startPt = polarToCartesian(cx, cy, radius, 0);
        const endPt = polarToCartesian(cx, cy, radius, sweep);
        const largeArcFlag = sweep > 180 ? 1 : 0;

        return [
            `M ${fmt(cx)} ${fmt(cy)}`,
            `L ${fmt(startPt.x)} ${fmt(startPt.y)}`,
            `A ${fmt(radius)} ${fmt(radius)} 0 ${largeArcFlag} 1 ${fmt(endPt.x)} ${fmt(endPt.y)}`,
            'Z',
        ].join(' ');
    }, [cx, cy, radius, sweep, isFull]);

    // 圆弧边框 path（当不是整圆时）
    const pathBorder = useMemo(() => {
        if (isFull || sweep === 0) return null;

        const startPt = polarToCartesian(cx, cy, radius, 0);
        const endPt = polarToCartesian(cx, cy, radius, sweep);
        const largeArcFlag = sweep > 180 ? 1 : 0;

        return [`M ${fmt(startPt.x)} ${fmt(startPt.y)}`, `A ${fmt(radius)} ${fmt(radius)} 0 ${largeArcFlag} 1 ${fmt(endPt.x)} ${fmt(endPt.y)}`].join(' ');
    }, [cx, cy, radius, sweep, isFull]);

    // 动态 id 避免冲突

    // 背景 gradient id 固定（或你也可以动态化）
    const bgGradId = 'paint0_radial_2752_127167';

    return (
        <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none">
            {/* ---------- 非 360°：先填充（在下），再描边（在上） ---------- */}
            {!isFull && pathCircle && (
                <>
                    {/* 填充扇形（在下） */}
                    <path d={pathCircle} fill={`url(#gidRadial-${angle})`} fillOpacity={0.5} />
                    {/* 描边弧（在上，保持不透明以保证亮度） */}
                    <path d={pathBorder!} stroke="white" strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </>
            )}

            {/* ---------- 360°：使用两个 circle，一个做填充（可半透明），一个单独做 stroke（保持亮度） ---------- */}
            {isFull && (
                <>
                    {/* 填充圆（半透明） */}
                    <circle cx={cx} cy={cy} r={radius} fill={`url(#gidRadial-${angle})`} fillOpacity={0.5} />
                    {/* 描边圆（不透明，保证亮度） */}
                    <circle cx={cx} cy={cy} r={radius} fill="none" stroke="white" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
                </>
            )}

            {/* ---------- 背景（始终显示） ---------- */}
            <circle opacity="0.2" cx="50.42" cy="50.3399" r="49.5105" fill={`url(#${bgGradId})`} stroke="white" strokeWidth="1.67832" />

            {/* ---------- defs: linear + radial + background radial ---------- */}
            <defs>
                <linearGradient id={`gidLinear`} x1="44.1662" y1="74.2498" x2="27.396" y2="5.01583" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#0040FF" />
                    <stop offset="1" stopColor="white" />
                </linearGradient>

                <radialGradient
                    id={`gidRadial-${angle}`}
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform={`translate(${cx} ${cy}) scale(${radius})`}
                >
                    <stop stopColor="white" stopOpacity="0" />
                    <stop offset="1" stopColor="#2659FF" />
                </radialGradient>

                <radialGradient
                    id={bgGradId}
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform={`translate(50.0004 50) rotate(33.0239) scale(47.7074)`}
                >
                    <stop stopColor="white" />
                    <stop offset="1" stopColor="#002290" />
                </radialGradient>
            </defs>
        </svg>
    );
};

export default Sector;
